import { html } from "lit";
import { customElement, state } from "lit/decorators.js";
import { repeat } from "lit/directives/repeat.js";
import { Api } from "../api";
import { BaseElement } from "../utils/ui-components";
import { InvertedIndex } from "../data/InvertedIndex";
import { MediaItem } from "../data/types";

@customElement("index-builder-page")
export class IndexBuilderPage extends BaseElement {
    @state()
    private loading = true;

    @state()
    private index: InvertedIndex = new InvertedIndex();

    @state()
    private stats = { documentCount: 0, vocabularySize: 0 };

    @state()
    private searchQuery = "";

    @state()
    private searchResults: Array<{ docId: string; score: number }> = [];

    @state()
    private items: Map<string, MediaItem> = new Map();

    @state()
    private timing = {
        fetchTime: 0,
        indexTime: 0,
        totalTime: 0,
    };

    protected async firstUpdated(): Promise<void> {
        await this.buildIndex();
    }

    private async buildIndex() {
        try {
            const startTotal = performance.now();

            // Measure fetch time
            const startFetch = performance.now();
            const items = await Api.getItems();
            // output the number of items that don't have subtitles
            const itemsWithoutSubtitles = items.filter((item) => {
                const firstSegment = item.segments?.[0];
                return !firstSegment || !firstSegment.subtitles;
            });
            console.log(`${itemsWithoutSubtitles.length} items without subtitles`);
            const fetchTime = performance.now() - startFetch;

            // Store items for later lookup
            items.forEach((item) => {
                this.items.set(item.id, item);
            });

            // Measure index building time
            const startIndex = performance.now();

            // Index each media item
            for (const item of items) {
                if (!item.segments?.length) {
                    // Index the whole item if no segments
                    this.index.addDocument({
                        id: item.id,
                        text: `${item.title} ${item.text}`,
                    });
                } else {
                    // Index each segment separately
                    for (const [idx, segment] of item.segments.entries()) {
                        this.index.addDocument({
                            id: `[${idx}]${item.id}`,
                            text: `${segment.title} ${segment.text}`,
                        });
                    }
                }
            }

            const indexTime = performance.now() - startIndex;
            const totalTime = performance.now() - startTotal;

            this.timing = {
                fetchTime,
                indexTime,
                totalTime,
            };

            this.stats = this.index.getStats();
        } catch (error) {
            console.error("Failed to build index:", error);
        } finally {
            this.loading = false;
        }
    }

    private handleSearch() {
        if (!this.searchQuery.trim()) {
            this.searchResults = [];
            return;
        }
        this.searchResults = this.index.search(this.searchQuery, 100);
    }

    private getItemForResult(docId: string): { item: MediaItem; segmentIndex?: number } {
        const match = docId.match(/^\[(\d+)\](.+)$/);

        if (match) {
            // This is a segment
            const [, indexStr, itemId] = match;
            const item = this.items.get(itemId);
            if (!item) throw new Error(`Item not found: ${itemId}`);

            return {
                item,
                segmentIndex: parseInt(indexStr),
            };
        } else {
            // This is a full document
            const item = this.items.get(docId);
            if (!item) throw new Error(`Item not found: ${docId}`);

            return { item };
        }
    }

    render() {
        return html`
            <div class="max-w-4xl mx-auto p-6">
                <div class="bg-white rounded-lg shadow-sm p-6 mb-6">
                    <h1 class="text-2xl font-bold mb-4">Inverted Index Builder</h1>

                    ${this.loading
                        ? html`<div class="text-gray-600">Building index...</div>`
                        : html`
                              <div class="mb-6 space-y-4">
                                  <div class="text-sm text-gray-600">
                                      Documents indexed: ${this.stats.documentCount}<br />
                                      Vocabulary size: ${this.stats.vocabularySize}
                                  </div>

                                  <div class="text-sm text-gray-600">
                                      Data fetch time: ${(this.timing.fetchTime / 1000).toFixed(2)}s<br />
                                      Index build time: ${(this.timing.indexTime / 1000).toFixed(2)}s<br />
                                      Total time: ${(this.timing.totalTime / 1000).toFixed(2)}s
                                  </div>
                              </div>

                              <div class="mb-6">
                                  <input
                                      type="text"
                                      .value=${this.searchQuery}
                                      @input=${(e: InputEvent) => {
                                          this.searchQuery = (e.target as HTMLInputElement).value;
                                          this.handleSearch();
                                      }}
                                      placeholder="Search the index..."
                                      class="w-full p-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  />
                              </div>

                              ${this.searchResults.length > 0
                                  ? html`
                                        <div class="space-y-4">
                                            ${repeat(
                                                this.searchResults,
                                                (result) => result.docId,
                                                (result) => {
                                                    const { item, segmentIndex } = this.getItemForResult(result.docId);
                                                    const segment = segmentIndex !== undefined ? item.segments?.[segmentIndex] : undefined;

                                                    return html`
                                                        <search-hit-view
                                                            .hit=${{
                                                                item,
                                                                matches: [
                                                                    {
                                                                        type: "main",
                                                                        highlights: {
                                                                            title: segment?.title || item.title,
                                                                            text: segment?.text || item.text,
                                                                        },
                                                                    },
                                                                ],
                                                            }}
                                                        ></search-hit-view>
                                                    `;
                                                }
                                            )}
                                        </div>
                                    `
                                  : this.searchQuery
                                  ? html`<div class="text-gray-600">No results found</div>`
                                  : null}
                          `}
                </div>
            </div>
        `;
    }
}
